import {DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useDeferredValue, useRef, useState, type ReactNode, type RefObject} from 'react';

import {sort} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useIsMobileView} from '../../../../hooks/useIsMobileView';
import {useColumnHidingAllowed, useColumnStates} from '../../../../store/selectors';
import {useEagGridStore} from '../../../../store/useEagGridStore';
import type {EagColumn} from '../../../../types/EagColumn';
import type {EagColumnOrGroup} from '../../../../types/EagColumnGroup';
import type {EagColumnState} from '../../../../types/EagColumnState';
import {scrollToColumn} from '../../../../utils/scrollToColumn';
import {HiddenColumnItem} from './components/HiddenColumnItem/HiddenColumnItem';
import {HighlightedColumnLabel} from './components/HighlightedColumnLabel/HighlightedColumnLabel';
import {SortableColumnItem} from './components/SortableColumnItem/SortableColumnItem';
import {applyColumnsVisibility} from './utils/applyColumnsVisibility';
import {buildColumnGroupMeta} from './utils/buildColumnGroupMeta';
import {filterFlatColumns} from './utils/filterFlatColumns';
import {groupHiddenColumns} from './utils/groupHiddenColumns';
import {getMatchedColumnKeys, getRankedMatchKeys} from './utils/matchColumnsBySearch';
import {scrollToPanelRow} from './utils/scrollToPanelRow';

export type ColumnsPanelProps = {
  columns: EagColumn[];
  columnConfig?: EagColumnOrGroup[];
  /** The grid's table element — enables the scroll-to-mode reveal-in-grid action. */
  tableRef?: RefObject<HTMLTableElement | null>;
  /**
   * Feature-flag gate (EAG_GRID_EXPERIMENTAL_COLUMNS_PANEL_SCROLL_TO via DmsDataGrid) for
   * the scroll-to features: the search-mode toggle, match cycling and the
   * per-row reveal-in-grid buttons. Off (default) = plain filtering search.
   */
  scrollToEnabled?: boolean;
};

/**
 * What the search input does: 'search' filters the list (original behavior),
 * 'scrollTo' keeps the full list, marks matches and scrolls to the first one.
 */
type SearchMode = 'search' | 'scrollTo';

const DRAG_ACTIVATION_DISTANCE = 8;

// Module-level ref callback (stable identity): focuses the search input when
// the panel mounts. rAF defers past the sidebar's mount/slide-in commit.
const focusOnMount = (node: HTMLInputElement | null) => {
  if (node) {
    requestAnimationFrame(() => node.focus());
  }
};

/** Borderless blue text link button (the "Show all"/"Hide all" header actions). */
function LinkButton(props: {
  onClick: () => void;
  disabled: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      // min-w-0 + truncate: long translations shorten with an ellipsis instead
      // of pushing the row (and their own hit area) outside the panel.
      className="block border-0 bg-transparent cursor-pointer text-[12px] font-medium p-0 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
      style={{color: '#0052CC', fontSize: '12px'}}
      title={typeof props.children === 'string' ? props.children : undefined}
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

/**
 * One half of the Search | Scroll-to segmented mode control. Icon-only with an
 * aria-label/tooltip: text labels can overflow the 360px panel in verbose
 * locales and push the button's hit area outside the panel.
 */
function ModeButton(props: {
  active: boolean;
  onClick: () => void;
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      // Quiet, borderless segments: inside the input a boxed control reads as
      // overflow — the active mode is a tinted pill instead.
      className={`border-0 cursor-pointer flex items-center justify-center h-full px-[11px] rounded-[4px] ${
        props.active ? '' : 'hover:bg-[#F4F5F7]'
      }`}
      style={
        props.active
          ? {backgroundColor: '#E9F2FF', color: '#0052CC'}
          : {backgroundColor: 'transparent', color: '#7A869A'}
      }
      aria-pressed={props.active}
      aria-label={props.label}
      title={props.label}
      onClick={props.onClick}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

/** Crosshair icon for the scroll-to mode button. */
function TargetIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="7" />
        <line x1="12" y1="2" x2="12" y2="5" />
        <line x1="12" y1="19" x2="12" y2="22" />
        <line x1="2" y1="12" x2="5" y2="12" />
        <line x1="19" y1="12" x2="22" y2="12" />
      </g>
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Compact chevron button cycling the current match (▲ prev / ▼ next). */
function CycleMatchButton(props: {direction: 'prev' | 'next'; onClick: () => void; label: string}) {
  const points = props.direction === 'prev' ? '6 15 12 9 18 15' : '6 9 12 15 18 9';
  return (
    <button
      type="button"
      className="border-0 bg-transparent cursor-pointer flex rounded-[4px] flex-shrink-0 hover:bg-[#EBECF0] hover:text-[#091E42]"
      style={{color: '#7A869A', padding: '4px'}}
      aria-label={props.label}
      title={props.label}
      onClick={props.onClick}
      data-testid={`columns-panel-match-${props.direction}`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="16.5" y1="16.5" x2="21" y2="21" />
      </g>
    </svg>
  );
}

// eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
export function ColumnsPanel(props: ColumnsPanelProps) {
  const t = useTranslationContext();
  const store = useEagGridStore();
  const columnStates = useColumnStates();
  const columnHidingAllowed = useColumnHidingAllowed();

  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState<SearchMode>('search');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const isMobileView = useIsMobileView();

  // The input is CONTROLLED by searchTerm, so its updates must stay urgent —
  // a transition-controlled input drops keystrokes whenever an urgent update
  // (e.g. resetting the match cycle) commits a render with the stale term.
  // The expensive derived rendering (filtering, match marks, rankings) reads
  // this deferred copy instead, which is what keeps typing responsive.
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  const groupMeta = buildColumnGroupMeta(props.columnConfig);
  const columnByKey = new Map(props.columns.map((col) => [col.key, col]));
  const columnStateByKey = new Map(columnStates.map((cs) => [cs.key, cs]));

  // lockedHidden (definition-hidden) columns are permanently hidden — never listed.
  const sortedColumns = sort(
    (a: EagColumnState, b: EagColumnState) => a.order - b.order,
    columnStates
  )
    .map((state) => columnByKey.get(state.key))
    .filter((col): col is EagColumn => col !== undefined && col.lockedHidden !== true);

  const isHidden = (key: string) => columnStateByKey.get(key)?.hidden ?? false;

  const shownColumns = sortedColumns.filter((col) => !isHidden(col.key));
  const hiddenColumns = sortedColumns.filter((col) => isHidden(col.key));
  const shownCount = shownColumns.length;

  const scrollToEnabled = props.scrollToEnabled === true;
  const isScrollToMode = scrollToEnabled && mode === 'scrollTo';
  // Search mode filters the list; scroll-to mode keeps it whole and instead
  // marks the matches (and scrolls to the first one — see scrollToKey).
  const filteredShown = isScrollToMode
    ? shownColumns
    : filterFlatColumns(shownColumns, deferredSearchTerm);
  const filteredHidden = isScrollToMode
    ? hiddenColumns
    : filterFlatColumns(hiddenColumns, deferredSearchTerm);
  const filteredShownIds = filteredShown.map((col) => col.key);
  const matchedKeys = isScrollToMode
    ? getMatchedColumnKeys(sortedColumns, deferredSearchTerm)
    : new Set<string>();
  // A search-mode term narrows the list to its matches; scroll-to mode never
  // filters. Drag is disabled while the list is filtered (reordering a filtered
  // subset would be ambiguous) and whenever column hiding is not allowed.
  const isListFiltered = !isScrollToMode && deferredSearchTerm.length > 0;
  const isDragDisabled = isListFiltered || !columnHidingAllowed;

  // Hidden columns grouped by their column group, in group declaration order,
  // with ungrouped columns rendered last (no sub-header).
  const hiddenBuckets = groupHiddenColumns(filteredHidden, props.columnConfig, groupMeta);

  // Full list in DOM order — the Shown section renders in columnStates order,
  // the Hidden section in bucket order. Always the FULL lists (the current
  // render may be search-mode filtered).
  const fullHiddenBuckets = groupHiddenColumns(hiddenColumns, props.columnConfig, groupMeta);
  const domOrderedColumns = [
    ...shownColumns,
    ...fullHiddenBuckets.flatMap((bucket) => bucket.columns),
  ];

  // Matches ranked strongest-first (exact > prefix > substring, DOM order
  // breaks ties); activeMatchIndex cycles through them via the counter's
  // prev/next buttons or Enter / Shift+Enter in the input.
  const rankedMatchKeys = isScrollToMode
    ? getRankedMatchKeys(domOrderedColumns, deferredSearchTerm)
    : [];
  const currentMatchIndex =
    rankedMatchKeys.length > 0 ? Math.min(activeMatchIndex, rankedMatchKeys.length - 1) : 0;
  const currentMatchKey = rankedMatchKeys[currentMatchIndex];

  // rAF so the jump runs after the un-filtered list has committed (relevant
  // when switching search -> scroll-to with a term already typed).
  const scrollToKey = (key: string | undefined) => {
    if (key == null) {
      return;
    }
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (list) {
        scrollToPanelRow(list, key);
      }
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Every edit restarts the cycle at the strongest match.
    setActiveMatchIndex(0);
    if (mode === 'scrollTo') {
      scrollToKey(getRankedMatchKeys(domOrderedColumns, value)[0]);
    }
  };

  const handleModeChange = (nextMode: SearchMode) => {
    setMode(nextMode);
    setActiveMatchIndex(0);
    if (nextMode === 'scrollTo') {
      scrollToKey(getRankedMatchKeys(domOrderedColumns, searchTerm)[0]);
    }
  };

  const handleCycleMatch = (direction: 1 | -1) => {
    if (rankedMatchKeys.length === 0) {
      return;
    }
    const nextIndex =
      (currentMatchIndex + direction + rankedMatchKeys.length) % rankedMatchKeys.length;
    setActiveMatchIndex(nextIndex);
    scrollToKey(rankedMatchKeys[nextIndex]);
  };

  // Reveal-in-grid: scrolls the data grid to the column and flashes it (the
  // same flash the toolbar Find Column feature uses). A hidden column is shown
  // first; its th only exists after that commit, hence the rAF.
  const handleRevealInGrid = (key: string) => {
    if (props.tableRef?.current == null) {
      return;
    }
    if (isHidden(key)) {
      store.setColumnHidden(key, false);
      // DOUBLE rAF: the un-hide triggers a re-render + DOM mutation, and a
      // single rAF fires before the new <th> is painted/queryable — same
      // hazard and fix as FindColumnButton.tsx.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const table = props.tableRef?.current;
          if (table) {
            scrollToColumn(table, key);
          }
        });
      });
      return;
    }
    scrollToColumn(props.tableRef.current, key);
  };
  const onRevealInGrid = scrollToEnabled && props.tableRef != null ? handleRevealInGrid : undefined;

  // Screen-reader announcement for the match cycle (aria-live via role=status).
  const currentMatchColumn = currentMatchKey != null ? columnByKey.get(currentMatchKey) : undefined;
  const matchStatusText = !isScrollToMode // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? ''
    : currentMatchColumn != null // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? t('page.datagrid.labels.matchAnnouncement', {
          current: currentMatchIndex + 1,
          total: rankedMatchKeys.length,
          column: currentMatchColumn.headerName,
        })
      : deferredSearchTerm.length > 0
        ? t('page.datagrid.labels.noMatch')
        : '';

  // Show all / Hide all act on the list the user sees: the search-matched
  // subset while a term filters it, otherwise every listed column. lockedHidden
  // columns are never listed, and applyColumnsVisibility skips them anyway.
  const handleShowAll = () => {
    const targets = isListFiltered ? filteredHidden : hiddenColumns;
    const eligibleKeys = new Set(targets.map((col) => col.key));
    store.setColumnStates(applyColumnsVisibility(columnStates, eligibleKeys, false));
  };

  const handleHideAll = () => {
    const targets = isListFiltered ? filteredShown : shownColumns;
    const eligibleKeys = new Set(
      targets.filter((col) => col.hideable !== false).map((col) => col.key)
    );
    store.setColumnStates(applyColumnsVisibility(columnStates, eligibleKeys, true));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = event.active.id as string;
    const overId = event.over?.id as string | undefined;
    if (!overId || activeId === overId) {
      return;
    }
    store.reorderColumnsByKey(activeId, overId);
  };

  const hasResults = filteredShown.length > 0 || filteredHidden.length > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Sticky search + tools */}
      <div
        className="flex flex-col gap-2 px-3 py-2 flex-shrink-0"
        style={{borderBottom: '1px solid #EBECF0'}}
      >
        <div
          className="flex items-center gap-2 h-8 px-[10px] rounded-[4px] text-[13px] focus-within:shadow-[0_0_0_4px_#BFE0FF]"
          style={{border: '1px solid #C1C7D0', backgroundColor: '#FFFFFF', color: '#091E42'}}
        >
          <span className="flex" style={{color: '#7A869A'}}>
            <SearchIcon />
          </span>
          <input
            // Autofocus is part of the flagged feature (master never moved
            // focus) and is suppressed in the mobile sheet, where programmatic
            // focus can pop the on-screen keyboard over the sheet.
            ref={scrollToEnabled && !isMobileView ? focusOnMount : undefined}
            role="searchbox"
            aria-label={t(
              isScrollToMode
                ? 'page.datagrid.labels.scrollToColumnsPlaceholder'
                : 'page.datagrid.labels.searchColumnsPlaceholder'
            )}
            className="flex-1 min-w-0 border-0 outline-0 bg-transparent text-[13px] placeholder:text-[#7A869A]"
            style={{color: '#091E42'}}
            placeholder={t(
              isScrollToMode
                ? 'page.datagrid.labels.scrollToColumnsPlaceholder'
                : 'page.datagrid.labels.searchColumnsPlaceholder'
            )}
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            onKeyDown={(event) => {
              // Escape clears an active term (both modes); an empty input
              // lets the event propagate (e.g. to close the panel).
              if (event.key === 'Escape') {
                if (searchTerm.length > 0) {
                  event.preventDefault();
                  event.stopPropagation();
                  setSearchTerm('');
                  setActiveMatchIndex(0);
                }
                return;
              }
              if (!isScrollToMode) {
                return;
              }
              // Enter / ArrowDown = next match, Shift+Enter / ArrowUp =
              // previous (preventDefault keeps the caret from jumping).
              if (event.key === 'Enter' || event.key === 'ArrowDown') {
                event.preventDefault();
                handleCycleMatch(event.key === 'Enter' && event.shiftKey ? -1 : 1);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                handleCycleMatch(-1);
              }
            }}
            data-testid="columns-panel-search"
          />
          <Show when={isScrollToMode && rankedMatchKeys.length > 0}>
            <span
              className="flex items-center gap-[3px] flex-shrink-0 pl-[8px]"
              style={{borderLeft: '1px solid #EBECF0'}}
            >
              <span
                className="text-[11px]"
                style={{color: '#7A869A', fontVariantNumeric: 'tabular-nums'}}
                data-testid="columns-panel-match-counter"
              >
                {currentMatchIndex + 1}/{rankedMatchKeys.length}
              </span>
              <CycleMatchButton
                direction="prev"
                onClick={() => handleCycleMatch(-1)}
                label={t('page.datagrid.labels.prevMatchAria')}
              />
              <CycleMatchButton
                direction="next"
                onClick={() => handleCycleMatch(1)}
                label={t('page.datagrid.labels.nextMatchAria')}
              />
            </span>
          </Show>
          {/* Mode toggle lives inside the search box, trailing, borderless
              (quiet segments — the active one is a tinted pill). Icon-only:
              translated text labels can be arbitrarily long and would push
              the buttons' hit areas outside the 360px panel. */}
          <Show when={scrollToEnabled}>
            <div
              className="inline-flex items-center flex-shrink-0 h-6 gap-[2px] pl-[8px]"
              style={{borderLeft: '1px solid #EBECF0'}}
              role="group"
              aria-label={t('page.datagrid.labels.searchModeToggleAria')}
            >
              <ModeButton
                active={!isScrollToMode}
                onClick={() => handleModeChange('search')}
                label={t('page.datagrid.labels.searchMode')}
                testId="columns-panel-mode-search"
              >
                <SearchIcon />
              </ModeButton>
              <ModeButton
                active={isScrollToMode}
                onClick={() => handleModeChange('scrollTo')}
                label={t('page.datagrid.labels.scrollToMode')}
                testId="columns-panel-mode-scrollTo"
              >
                <TargetIcon />
              </ModeButton>
            </div>
          </Show>
        </div>
        <div className="flex items-center justify-end min-w-0">
          <div className="flex items-center gap-[6px] min-w-0">
            <LinkButton
              onClick={handleShowAll}
              disabled={!columnHidingAllowed}
              testId="columns-panel-show-all"
            >
              {t('page.datagrid.labels.showAllColumns')}
            </LinkButton>
            <span className="flex-shrink-0" style={{color: '#C1C7D0'}}>
              ·
            </span>
            <LinkButton
              onClick={handleHideAll}
              disabled={!columnHidingAllowed}
              testId="columns-panel-hide-all"
            >
              {t('page.datagrid.labels.hideAllColumns')}
            </LinkButton>
          </div>
        </div>
        <Show when={isScrollToMode && deferredSearchTerm.length > 0 && matchedKeys.size === 0}>
          <div className="text-[13px]" style={{color: '#7A869A'}} data-testid="columns-panel-empty">
            {t('page.datagrid.labels.noMatch')}
          </div>
        </Show>
        {/* Always mounted so assistive tech reliably picks up updates. */}
        <span className="sr-only" role="status" data-testid="columns-panel-match-status">
          {matchStatusText}
        </span>
      </div>

      {/* Scrollable column list. No top padding: a `sticky top-0` section header
          would otherwise pin below the padding, leaving a transparent band where
          scrolled rows show through. The section headers carry their own `pt-2`. */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-2 pb-2">
        <Show when={filteredShown.length > 0}>
          <div className="mb-1">
            <div
              className="flex items-center justify-between px-2 pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.05em] sticky top-0 z-[1]"
              style={{color: '#7A869A', backgroundColor: '#FFFFFF'}}
            >
              <span>{t('page.datagrid.labels.shownColumns')}</span>
              <span style={{fontVariantNumeric: 'tabular-nums'}}>{shownCount}</span>
            </div>
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext items={filteredShownIds} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-0.5">
                  {filteredShown.map((column) => {
                    const columnState = columnStateByKey.get(column.key);
                    if (!columnState) {
                      return null;
                    }
                    const meta = groupMeta.get(column.key);
                    return (
                      <SortableColumnItem
                        key={column.key}
                        column={column}
                        columnState={columnState}
                        groupLabel={meta?.label}
                        isAvailabilityTag={meta?.isAvailability}
                        onSetHidden={store.setColumnHidden}
                        columnHidingAllowed={columnHidingAllowed}
                        isDragDisabled={isDragDisabled}
                        isSearchMatch={matchedKeys.has(column.key)}
                        isCurrentMatch={column.key === currentMatchKey}
                        searchTerm={deferredSearchTerm}
                        onRevealInGrid={onRevealInGrid}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </Show>

        <Show when={filteredHidden.length > 0}>
          <div className="mb-1">
            <div
              className="flex items-center justify-between px-2 pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.05em] sticky top-0 z-[1]"
              style={{color: '#7A869A', backgroundColor: '#FFFFFF'}}
            >
              <span>{t('page.datagrid.labels.hiddenColumns')}</span>
              <span style={{fontVariantNumeric: 'tabular-nums'}}>{hiddenColumns.length}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              {hiddenBuckets.map((bucket) => (
                <div key={bucket.groupId}>
                  <Show when={isNotNilOrEmpty(bucket.label) ? bucket.label : null}>
                    {(label) => (
                      <div
                        className="text-[11px] font-semibold px-2 pt-1.5 pb-0.5"
                        style={{color: '#42526E'}}
                      >
                        <HighlightedColumnLabel text={label} searchTerm={deferredSearchTerm} />
                      </div>
                    )}
                  </Show>
                  {bucket.columns.map((column) => (
                    <HiddenColumnItem
                      key={column.key}
                      column={column}
                      onSetHidden={store.setColumnHidden}
                      columnHidingAllowed={columnHidingAllowed}
                      isSearchMatch={matchedKeys.has(column.key)}
                      isCurrentMatch={column.key === currentMatchKey}
                      searchTerm={deferredSearchTerm}
                      onRevealInGrid={onRevealInGrid}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Show>

        <Show when={!hasResults}>
          <div
            className="text-[13px] px-2 py-2"
            style={{color: '#7A869A'}}
            data-testid="columns-panel-empty"
          >
            {t('page.datagrid.labels.noMatch')}
          </div>
        </Show>
      </div>
    </div>
  );
}
